/**
 * File: useInvoiceLedgerQueries.ts
 * Path: apps/admin/src/shared/hooks/useInvoiceLedgerQueries.ts
 * Description: Every invoice as the books hold it (invoiceLedger.ts), and
 * the contract payments not invoiced yet (stillToBill.ts). Money, the
 * accounting overview, Aged debtors, Today and the client page all read
 * these, so the debtors figure is one figure everywhere: the Trade debtors
 * account. Reading the books needs the finance permission; without it the
 * ledger is "unavailable" and the pages say so. useOwedFigures gives the
 * two figures every page shows: Owed to you and Still to bill.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  COMPANY_PROFILE_ID,
  CREDIT_NOTE_COLLECTIONS,
  buildInvoiceLedger,
  southAfricanDay,
  stillToBill,
  type CreditNote,
  type InvoiceLedger,
  type Journal,
  type LedgerAccount,
  type LedgerLink,
  type SavedInvoice,
  type StillToBill,
} from "@idk/config";
import { useRole } from "@/modules/auth/hooks/useRoleQueries";
import { contractsRepo } from "../services/contractsService";
import { listCreditNotes } from "../services/invoiceBooksService";
import { useAccounts, useJournals } from "./useBooksQueries";
import { useAllContractPayments, useContractsList } from "./useContractsQueries";
import { invoiceLinksKey } from "./useBooksInvoicesQueries";
import { useInvoices } from "./useInvoicesQueries";
import { owedFigures, owedFiguresByClient, type OwedFigures, type OwedScope } from "../utils/owedUtils";

export const creditNotesKey = [CREDIT_NOTE_COLLECTIONS.creditNotes] as const;

export interface LedgerData {
  /** Null while loading; "unavailable" when the books can't be read (no finance permission, or not set up). */
  ledger: InvoiceLedger | null | "unavailable";
  journals: readonly Journal[];
  accounts: readonly LedgerAccount[];
  saved: readonly SavedInvoice[];
  creditNotes: readonly CreditNote[];
  links: readonly LedgerLink[];
  today: string;
  failed: boolean;
  refetch: () => void;
}

/**
 * `asAt` reads the books as they stood on that day (a report's date); today
 * when left out. `wanted` false skips reading the books (a page showing the
 * personal books).
 */
export const useInvoiceLedger = (asAt?: string, wanted = true): LedgerData => {
  const [today] = useState(() => southAfricanDay(Date.now()));
  const day = asAt ?? today;
  const { can } = useRole();
  const canRead = can("finance") && wanted;
  const journals = useJournals(COMPANY_PROFILE_ID, canRead);
  const accounts = useAccounts(COMPANY_PROFILE_ID, canRead);
  const saved = useInvoices();
  const links = useQuery({ queryKey: invoiceLinksKey, queryFn: contractsRepo.allInvoiceLinks });
  const notes = useQuery({ queryKey: creditNotesKey, queryFn: listCreditNotes });

  const linkList = useMemo<LedgerLink[]>(
    () => Object.entries(links.data ?? {}).flatMap(([contractId, list]) => list.map((l) => ({ contractId, reference: l.reference, number: l.number }))),
    [links.data],
  );
  const ledger = useMemo<LedgerData["ledger"]>(() => {
    if (!canRead || journals.error || accounts.error) return "unavailable";
    if (!journals.data || !accounts.data || !saved.data || !links.data) return null;
    if (accounts.data.length === 0) return "unavailable";
    return buildInvoiceLedger({ journals: journals.data, accounts: accounts.data, saved: saved.data, links: linkList, asAt: day });
  }, [canRead, journals.data, journals.error, accounts.data, accounts.error, saved.data, links.data, linkList, day]);

  return {
    ledger,
    journals: journals.data ?? [],
    accounts: accounts.data ?? [],
    saved: saved.data ?? [],
    creditNotes: notes.data ?? [],
    links: linkList,
    today,
    failed: Boolean(saved.error || links.error),
    refetch: () => {
      void journals.refetch();
      void accounts.refetch();
      void saved.refetch();
      void links.refetch();
      void notes.refetch();
    },
  };
};

/**
 * Payment references an invoice already covers: any invoice the books hold
 * for it that isn't void, a draft waiting in Ready to send, or a linked Zoho
 * invoice.
 */
export const invoicedReferences = (ledger: InvoiceLedger | null | "unavailable", saved: readonly SavedInvoice[], links: readonly LedgerLink[]): Set<string> => {
  const refs = new Set<string>(links.map((l) => l.reference));
  for (const s of saved) {
    if (s.status === "draft" || s.status === "issued") refs.add(s.draft.paymentReference);
  }
  if (ledger && ledger !== "unavailable") {
    for (const r of ledger.invoices) if (r.status !== "void" && r.paymentReference) refs.add(r.paymentReference);
  }
  refs.delete("");
  return refs;
};

/** Contract payments not invoiced yet; null while loading. */
export const useStillToBill = (data: Pick<LedgerData, "ledger" | "saved" | "links" | "today">): StillToBill | null => {
  const contracts = useContractsList();
  const payments = useAllContractPayments();
  return useMemo(() => {
    if (!contracts.data || !payments.data) return null;
    return stillToBill({
      contracts: contracts.data,
      receipts: payments.data.receipts,
      dueDates: payments.data.dueDates,
      invoiced: invoicedReferences(data.ledger, data.saved, data.links),
      today: data.today,
    });
  }, [contracts.data, payments.data, data.ledger, data.saved, data.links, data.today]);
};

/** Owed to you and Still to bill (owedUtils.ts), for the business or one client. */
export const useOwedFigures = (scope?: OwedScope): OwedFigures => {
  const data = useInvoiceLedger();
  const toBill = useStillToBill(data);
  const clientId = scope?.clientId;
  const name = scope?.name;
  return useMemo(
    () => owedFigures(data.ledger, toBill, clientId !== undefined && name !== undefined ? { clientId, name } : undefined),
    [data.ledger, toBill, clientId, name],
  );
};

/** Owed to you and Still to bill for every client in a list, from one read of the books (the same sums as useOwedFigures with a scope). */
export const useOwedByClient = (clients: ReadonlyArray<{ id: string; name: string }>): Map<string, OwedFigures> => {
  const data = useInvoiceLedger();
  const toBill = useStillToBill(data);
  return useMemo(() => owedFiguresByClient(data.ledger, toBill, clients), [data.ledger, toBill, clients]);
};
